import React, { ReactNode } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronRight, ChevronDown, FileText, FolderIcon } from "lucide-react";
import { Account } from "./useChartOfAccountsController";

interface AccountsTableProps {
  accounts: Account[];
  onToggleExpand: (accountId: string) => void;
  searchTerm: string;
}

export const AccountsTable: React.FC<AccountsTableProps> = ({
  accounts,
  onToggleExpand,
  searchTerm,
}) => {
  // Function to check if an account or any of its children match the search term
  const accountMatchesSearch = (account: Account): boolean => {
    const isParent = account.children && account.children.length > 0;

    // Match in current account
    const currentAccountMatches =
      !searchTerm ||
      account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.code.includes(searchTerm);

    // Check children recursively if this is a parent account
    if (isParent && !currentAccountMatches) {
      return account.children!.some((child) => accountMatchesSearch(child));
    }

    return currentAccountMatches;
  };

  // Function to render accounts recursively
  const renderAccounts = (
    accounts: Account[],
    level: number = 0
  ): React.ReactElement[] => {
    return accounts.flatMap((account) => {
      const hasChildren = account.children && account.children.length > 0;
      const isExpanded = account.isExpanded || false;

      // Check if this account matches search criteria
      const matchesSearch = accountMatchesSearch(account);

      // Skip if it doesn't match search and it's not a parent with matching children
      if (searchTerm && !matchesSearch) {
        return [];
      }

      const rows: React.ReactElement[] = [
        <TableRow key={account.id} className="hover:bg-muted/50">
          <TableCell>
            <div
              className="flex items-center cursor-pointer"
              style={{ paddingLeft: `${level * 16}px` }}
              onClick={() => hasChildren && onToggleExpand(account.id)}
            >
              {hasChildren ? (
                <span className="mr-2">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </span>
              ) : (
                <span className="mr-2 ml-4">
                  <FileText className="h-4 w-4" />
                </span>
              )}
              {hasChildren && (
                <FolderIcon className="h-4 w-4 mr-2 text-blue-500" />
              )}
              <span className="font-mono">{account.code}</span>
            </div>
          </TableCell>
          <TableCell>{account.name}</TableCell>
        </TableRow>,
      ];

      // If expanded and has children, recursively render children
      if (isExpanded && hasChildren && account.children) {
        rows.push(...renderAccounts(account.children, level + 1));
      }

      return rows;
    });
  };

  return (
    <div className="overflow-auto" style={{ height: "70vh", width: "100%" }}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/3">Account Code</TableHead>
            <TableHead>Account Name</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{renderAccounts(accounts)}</TableBody>
      </Table>
    </div>
  );
};
